import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
  swatch?: ReactNode;
}

/**
 * The application's own dropdown: a button that opens a list of options. A native select can only hold text,
 * and for the colour of a code - or a ground that is see-through - the look is the choice, so every option
 * carries a swatch beside it.
 *
 * The keyboard is the select-only combobox pattern: focus stays on the button throughout, with
 * aria-activedescendant pointing at the option in hand.
 */
export function Dropdown<T extends string>({
  id,
  labelledBy,
  value,
  options,
  onChange,
  disabled = false,
  title,
  variant = 'compact',
}: {
  id: string;
  /** Id of the visible label; it names the button, so there is no second copy of the words to drift. */
  labelledBy: string;
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  title?: string;
  /** `compact` is the quiet control in the strip under a code; `field` is the size and shape of a form input. */
  variant?: 'compact' | 'field';
}) {
  const [open, setOpen] = useState(false);
  const selected = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [active, setActive] = useState(selected);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const current = options[selected] ?? options[0];

  useEffect(() => {
    if (!open) {
      return;
    }

    const dismiss = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);

  function show() {
    setActive(selected);
    setOpen(true);
  }

  function take(index: number) {
    const option = options[index];

    if (option) {
      onChange(option.value);
    }

    setOpen(false);
    button.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();

        if (!open) {
          show();
          return;
        }

        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((index) => Math.min(options.length - 1, Math.max(0, index + step)));
        return;
      }

      case 'Home':
        if (open) {
          event.preventDefault();
          setActive(0);
        }

        return;

      case 'End':
        if (open) {
          event.preventDefault();
          setActive(options.length - 1);
        }

        return;

      case 'Enter':
      case ' ':
        event.preventDefault();

        if (open) {
          take(active);
        } else {
          show();
        }

        return;

      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }

        return;

      case 'Tab':
        setOpen(false);
        return;

      default:
        return;
    }
  }

  return (
    <div className={`picker${variant === 'field' ? ' picker--field' : ''}`} ref={root}>
      <button
        type="button"
        id={id}
        ref={button}
        className="picker__button"
        role="combobox"
        aria-labelledby={labelledBy}
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        disabled={disabled}
        title={title}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={handleKeyDown}
      >
        {current?.swatch}
        <span className="picker__value">{current?.label}</span>
        <i className="pi pi-chevron-down picker__chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul className="picker__menu" id={listId} role="listbox" aria-labelledby={labelledBy}>
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={`picker__option${index === active ? ' is-active' : ''}`}
              // The button keeps the focus, so the list is driven by pointer and keyboard alike.
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => take(index)}
            >
              {option.swatch}
              <span className="picker__value">{option.label}</span>
              {option.value === value && <i className="pi pi-check picker__tick" aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
